import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import Colors from '../../utils/color';

/**
 * PULSE METER TILE (HITL 3.0) — shared presentational chip for the Pulse card's
 * time meters (Menu Board Time, Response time).
 *
 * Renders a value + label + a row of `barCount` segments that fill in proportion
 * to `elapsedMs / limitMs`, colour-coded by how close the elapsed time is to the
 * limit:
 *   0–50%   green (emerald)
 *   51–80%  amber
 *   81%+    red   (bittersweet) — over the limit it stays red (capped) so the
 *           breach reads clearly without a distracting blink.
 *
 * Purely presentational: the owning wrapper computes `elapsedMs` (and its tick
 * cadence), decides visibility, and supplies the `formatValue` for the numeral.
 */

const BAND = { green: 'green', yellow: 'yellow', red: 'red' } as const;
type Band = (typeof BAND)[keyof typeof BAND];

const bandForPct = (pct: number): Band => {
  if (pct <= 50) return BAND.green;
  if (pct <= 80) return BAND.yellow;
  return BAND.red; // 81–100% and anything over the limit
};

const bandColor = (band: Band): string => {
  switch (band) {
    case BAND.green:
      return Colors.emerald; // #30d158
    case BAND.yellow:
      // Warm amber (matches the Pulse mockup + the express-timer warning colour)
      // — reads better than a bright canary yellow.
      return Colors.amber; // #ffc107
    default:
      return Colors.bittersweet; // #FC7373
  }
};

const useStyles = makeStyles((theme: Theme) => ({
  tile: {
    // A compact chip, sized to its content — never stretched across the card.
    width: 'fit-content',
    // Breathing room below the header divider (self-contained so an empty metric
    // row leaves no stray gap).
    marginTop: theme.spacing(1.25),
    // A clean white chip (defined by its border), so nothing reads as a grey block.
    backgroundColor: Colors.white,
    border: `1px solid ${Colors.alto}`,
    borderRadius: '10px',
    padding: theme.spacing(1.25, 1.75),
    display: 'flex',
    flexDirection: 'column',
  },
  value: {
    fontSize: '1.6rem',
    fontWeight: 800,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.01em',
  },
  label: {
    marginTop: theme.spacing(0.75),
    fontSize: '0.72rem',
    color: Colors.mineShaft,
    whiteSpace: 'nowrap',
  },
  bars: {
    marginTop: theme.spacing(1.25),
    display: 'flex',
    gap: '4px',
  },
  bar: {
    // Fixed-width segments so the fit-content tile has a definite size.
    width: '16px',
    height: '10px',
    borderRadius: '3px',
    // A soft, light track (on the white chip) so empty segments read as light —
    // never a dark grey block — and the coloured fill pops against it.
    backgroundColor: Colors.gallery,
    overflow: 'hidden',
  },
  barFill: {
    display: 'block',
    height: '100%',
    borderRadius: '3px',
    transition: 'width 40ms linear',
  },
}));

export interface PulseMeterTileProps {
  label: string;
  /** Elapsed time to display, in ms. */
  elapsedMs: number;
  /** Threshold in ms; <= 0 disables colour escalation (stays green). */
  limitMs: number;
  /** Number of segments in the bar row. */
  barCount: number;
  /** Formats the numeral (e.g. "1:05" or "4.5s"). */
  formatValue: (ms: number) => string;
  testId?: string;
}

const PulseMeterTile = ({
  label,
  elapsedMs,
  limitMs,
  barCount,
  formatValue,
  testId,
}: PulseMeterTileProps) => {
  const classes = useStyles();

  const pct = limitMs > 0 ? (elapsedMs / limitMs) * 100 : 0;
  const band = bandForPct(pct);
  const color = bandColor(band);
  const fillUnits = (pct / 100) * barCount;

  return (
    <div className={classes.tile} data-testid={testId} data-band={band}>
      <div className={classes.value} style={{ color }}>
        {formatValue(elapsedMs)}
      </div>
      <div className={classes.label}>{label}</div>
      <div className={classes.bars}>
        {Array.from({ length: barCount }).map((_, i) => {
          const frac = Math.max(0, Math.min(1, fillUnits - i));
          return (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              data-testid="pulse-meter-bar"
              className={classes.bar}
            >
              <span
                data-testid="pulse-meter-bar-fill"
                className={classes.barFill}
                style={{ width: `${frac * 100}%`, backgroundColor: color }}
              />
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default PulseMeterTile;
